import { LogoMark } from "@/components/Logo";
import { useSettings, resolveImageUrl } from "@/context/SettingsContext";
import { Flame, Users, Dices, Beer, ExternalLink } from "lucide-react";

const PILLAR_ICONS = [Flame, Users, Dices, Beer];

export default function AboutPage() {
  const { settings, t } = useSettings();
  const logoSrc = resolveImageUrl(settings.logo_url);

  return (
    <div className="px-4 sm:px-8 py-12 sm:py-20 max-w-6xl mx-auto">
      <section className="grid lg:grid-cols-[1fr_auto] gap-10 lg:gap-20 items-center" data-testid="about-hero">
        <div className="animate-fade-up">
          <div className="text-[10px] tracking-[0.5em] uppercase text-crimson-bright mb-6" data-testid="about-tagline">{t("about.tagline")}</div>
          <h1 className="font-display font-black tracking-[0.08em] uppercase text-4xl sm:text-5xl lg:text-6xl leading-tight" data-testid="about-title">
            {t("about.title1")}
            <br />
            <span className="text-silver">{t("about.title2")}</span>
          </h1>
          <div className="tavern-divider my-8" />
          <p className="text-zinc-300 text-base sm:text-lg max-w-xl leading-relaxed whitespace-pre-wrap" data-testid="about-hero-text">
            {t("about.hero")}
          </p>
        </div>
        <div className="flex items-center justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-crimson/25 blur-3xl rounded-full" />
            <div className="relative">
              {logoSrc ? (
                <div
                  className="w-56 h-56 sm:w-72 sm:h-72 rounded-full overflow-hidden"
                  style={{ border: "2px solid rgba(192,192,192,0.4)", boxShadow: "0 0 40px rgba(136,8,8,0.4)" }}
                  data-testid="about-logo"
                >
                  <img src={logoSrc} alt="Ballhoot’s Tavern" className="w-full h-full object-cover" />
                </div>
              ) : (
                <LogoMark size={240} />
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {PILLAR_ICONS.map((Icon, i) => {
          const url = t(`about.p${i + 1}_url`).trim();
          const clickable = /^https?:\/\//i.test(url);
          const Tag = clickable ? "a" : "div";
          const linkProps = clickable ? { href: url, target: "_blank", rel: "noopener noreferrer" } : {};
          return (
          <Tag
            key={i}
            {...linkProps}
            className={`tavern-card p-6 group hover:border-crimson/60 transition-all animate-fade-up block ${clickable ? "cursor-pointer hover:-translate-y-0.5" : ""}`}
            style={{ animationDelay: `${i * 80}ms` }}
            data-testid={`pillar-${i + 1}`}
          >
            {t(`about.p${i + 1}_img`) ? (
              <img
                src={resolveImageUrl(t(`about.p${i + 1}_img`))}
                alt={t(`about.p${i + 1}_title`)}
                className="h-16 max-w-full object-contain mb-4"
                data-testid={`pillar-${i + 1}-img`}
              />
            ) : (
              <Icon size={22} className="text-crimson-bright mb-4" />
            )}
            <div className="font-display font-bold tracking-[0.25em] uppercase text-sm text-white mb-2">{t(`about.p${i + 1}_title`)}</div>
            <p className="text-sm text-zinc-400 leading-relaxed">{t(`about.p${i + 1}_desc`)}</p>
            {clickable && (
              <span className="mt-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-crimson-bright">
                Visit <ExternalLink size={12} />
              </span>
            )}
          </Tag>
          );
        })}
      </section>

      <section className="mt-20 tavern-card p-8 sm:p-10 relative overflow-hidden" data-testid="house-rules">
        <div className="absolute -right-20 -top-20 w-60 h-60 bg-crimson/10 blur-3xl rounded-full" />
        <div className="relative">
          <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-3">{t("about.rules_eyebrow")}</div>
          <h2 className="font-display font-bold tracking-[0.1em] uppercase text-2xl sm:text-3xl text-white mb-6">
            {t("about.rules_title")} <span className="text-crimson">{t("about.rules_accent")}</span>
          </h2>
          <ol className="space-y-4 text-zinc-300">
            {[1, 2, 3, 4].map((n) => (
              <li key={n} className="flex gap-4">
                <span className="font-display text-crimson-bright font-bold">{String(n).padStart(2, "0")}</span>
                <span className="leading-relaxed">{t(`about.rule${n}`)}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {t("about.extra") && (
        <section className="mt-10 tavern-card p-8 sm:p-10 animate-fade-up" data-testid="about-custom">
          <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-3">{t("about.extra_label")}</div>
          <div className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{t("about.extra")}</div>
        </section>
      )}
    </div>
  );
}
